import React, { useState, useEffect, useRef } from 'react';
import { API_BASE } from '../../config';
import { useDomainValidation } from '../../hooks/useDomainValidation';
import DomainValidationModal from '../DomainValidationRetryModal';
import { handleAIError } from '../../utils/aiErrorHandler';
import CostChip from '../CostChip';

import { useEscapeToClose } from '../../hooks/useEscapeToClose';
function MedicalFileForm({ onClose, onSave, fetchWithAuth, editingMedicalFile }) {
  useEscapeToClose(onClose);   // Escape dismisses this modal
  const { domainValidation, handleValidationResponse, clearValidation } = useDomainValidation();
  const [formData, setFormData] = useState({
    record_name: '',
    record_type: '',
    description: '',
    record_date: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (editingMedicalFile) {
      setFormData({
        record_name: editingMedicalFile.record_name || '',
        // Legacy rows carry the pre-2026-08-21 plural. Normalise on load so re-saving an existing
        // record cannot silently blank its type (no <option> matches 'lab_results' any more).
        record_type: (editingMedicalFile.record_type === 'lab_results'
          ? 'lab_result'
          : editingMedicalFile.record_type) || '',
        description: editingMedicalFile.description || '',
        record_date: editingMedicalFile.record_date ?
          new Date(editingMedicalFile.record_date).toISOString().split('T')[0] : ''
      });
    }
  }, [editingMedicalFile]);

  const validateFile = (file) => {
    const allowedTypes = [
      'application/pdf',
      'image/jpeg', 'image/png', 'image/tiff',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];
    const maxSize = 50 * 1024 * 1024;
    if (!allowedTypes.includes(file.type)) {
      alert('File type not allowed. Please use PDF, images (JPG, PNG, TIFF), Word documents, or text files.');
      return false;
    }
    if (file.size > maxSize) {
      alert('File too large. Maximum size is 50MB.');
      return false;
    }
    return true;
  };

  const handleFileSelect = (file) => {
    if (validateFile(file)) {
      setSelectedFile(file);
      if (!formData.record_name) {
        setFormData(prev => ({ ...prev, record_name: file.name }));
      }
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) handleFileSelect(files[0]);
  };
  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) handleFileSelect(files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsUploading(true);
    try {
      if (selectedFile) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', selectedFile);
        uploadFormData.append('record_type', formData.record_type);
        uploadFormData.append('description', formData.description);
        const response = await fetch(`${API_BASE}/medical-records/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          body: uploadFormData
        });
        if (await handleAIError(response)) {
          setIsUploading(false);
          return;
        }
        const validationResult = await handleValidationResponse(response, 'medical record');
        if (validationResult === true) { onSave(); onClose(); }
        else if (validationResult === null) {
          const error = await response.text();
          alert(`Upload failed: ${error}`);
        }
      } else if (editingMedicalFile) {
        const submitData = {
          record_name: formData.record_name,
          record_type: formData.record_type,
          description: formData.description,
          record_date: formData.record_date === '' ? null : formData.record_date
        };
        const response = await fetchWithAuth(`${API_BASE}/medical-records/${editingMedicalFile.id}`, {
          method: 'PUT',
          body: JSON.stringify(submitData)
        });
        if (response.ok) { onSave(); onClose(); }
      } else {
        const submitData = {
          record_name: formData.record_name,
          record_type: formData.record_type,
          description: formData.description,
          record_date: formData.record_date === '' ? null : formData.record_date
        };
        const response = await fetchWithAuth(`${API_BASE}/medical-records/`, {
          method: 'POST',
          body: JSON.stringify(submitData)
        });
        const validationResult2 = await handleValidationResponse(response, 'medical record');
        if (validationResult2 === true) { onSave(); onClose(); }
        else if (validationResult2 === null) {
          const errorText = await response.text();
          console.error('Create record error:', errorText);
          alert(`Failed to create record: ${errorText}`);
        }
      }
    } catch (error) {
      console.error('Failed to save medical file:', error);
      alert('Failed to save medical file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>{domainValidation && (
      <DomainValidationModal
        isOpen={true}
        mode={domainValidation.mode}
        rejectionMessage={domainValidation.message}
        entityType={domainValidation.entityType}
        onClose={clearValidation}
      />
    )}<div className="modal-overlay">
      <div className="modal-content mfm-modal" >
        <div className="modal-header">
          <h3>{editingMedicalFile ? 'Edit Medical Record' : 'Add Medical Record / File'}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          {!editingMedicalFile && (
            <div className="form-group">
              <label className="form-label">Upload File (Optional)</label>
              <div
                className={`file-drop-zone ${isDragging ? 'dragging' : ''} mfm-dropZone`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                 style={{ backgroundColor: isDragging ? '#f0f8ff' : '#fafafa', borderColor: isDragging ? 'var(--color-success)' : '#ccc' }}
              >
                {selectedFile ? (
                  <div>
                    <p className="mfm-fileSelected">✓ {selectedFile.name}</p>
                    <p className="mfm-fileSize">
                      Size: {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                    <p className="text-helper-xs">
                      Click to select a different file
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="mfm-dragText">
                      Drag & drop your medical file here
                    </p>
                    <p className="mfm-browseText">or click to browse</p>
                    <p className="text-helper-xs">
                      Supported: PDF, Images (JPG, PNG, TIFF), Word documents, Text files<br/>
                      Maximum size: 50MB
                    </p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileInputChange}
                accept=".pdf,.jpg,.jpeg,.png,.tiff,.doc,.docx,.txt"
                className="hidden"
              />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Record Name / Title *</label>
            <input
              type="text"
              className="form-input"
              value={formData.record_name}
              onChange={(e) => setFormData(prev => ({...prev, record_name: e.target.value}))}
              placeholder="e.g. Blood Test Results, MRI Scan, Doctor Visit Notes"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Record Type</label>
            <select
              className="form-select"
              value={formData.record_type}
              onChange={(e) => setFormData(prev => ({...prev, record_type: e.target.value}))}
            >
              <option value="">Select type...</option>
              {/* CANONICAL VALUE — must stay singular. 'lab_result' is what the clinical-sync
                  path writes (device_integration.py type_map) and what fhir_service._get_lab_results
                  and report_data_extractor recognise. This read 'lab_results' (plural) until
                  2026-08-21, which matched no consumer. See migration 116. */}
              <option value="lab_result">Lab Results</option>
              <option value="imaging">Imaging (X-ray, MRI, CT)</option>
              <option value="consultation">Doctor Consultation</option>
              <option value="vaccination">Vaccination Record</option>
              <option value="prescription">Prescription</option>
              <option value="insurance">Insurance Document</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
              placeholder="Additional details about this medical record"
              rows="4"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Date</label>
            <input
              type="date"
              className="form-input"
              value={formData.record_date}
              onChange={(e) => setFormData(prev => ({...prev, record_date: e.target.value}))}
            />
          </div>

          <div className="flex gap-2">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isUploading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isUploading}>
              {isUploading ? 'Uploading...' : (<>{editingMedicalFile ? 'Update Record' : 'Save Record'}{selectedFile && <> <CostChip apiType="medical_record" /></>}</>)}
            </button>
          </div>
        </form>
      </div>
    </div></>
  );
}

export default MedicalFileForm;
